export type Category = {
  href: string;
  slug: string;
  name: string;
  isActive: boolean;
  isCurrent: boolean;
  count: number | null;
  imageUrl: string | null;
};

export type Ancestor = {
  slug: string;
  name: string;
  path: string;
  type: string;
};

type BranchLike = {
  path: string;
  seoTaxonomies: { name: string; slug: string; type: string }[];
  totalRecipeCount: number;
  imageUrl: string | null;
};

export type FlatCategoryNode = {
  path: string;
  slug: string;
  name: string;
  depth: number;
  type: string;
  count: number | null;
};

const calculateDepth = (path: string): number => {
  return path.split("/").length;
};

const getSlugFromPath = (path: string): string => {
  return path.split("/").pop() ?? "";
};

const getNameFromTaxonomies = (taxonomies: { name: string }[]): string => {
  return taxonomies[taxonomies.length - 1]?.name || "";
};

const getTypeFromTaxonomies = (taxonomies: { type: string }[]): string => {
  return taxonomies[taxonomies.length - 1]?.type || "";
};

class TreeNode {
  public isRoot = false;
  public children: TreeNode[];
  public path: string | null;
  public count: number | null;
  public slug: string;
  public name: string;
  public depth: number;
  public type: string;

  constructor(
    private tree: Tree,
    private parent: TreeNode | null,
    branch: BranchLike | null,
    branches: BranchLike[],
  ) {
    if (branch) {
      this.depth = calculateDepth(branch.path);

      this.slug = getSlugFromPath(branch.path);
      this.name = getNameFromTaxonomies(branch.seoTaxonomies);
      this.type = getTypeFromTaxonomies(branch.seoTaxonomies);

      this.path = branch.path ?? null;
      this.count = branch.totalRecipeCount ?? null;
    } else {
      this.depth = 0;

      this.slug = "";
      this.name = "";
      this.type = "";

      this.path = null;
      this.count = null;

      this.isRoot = true;
    }

    const children = branches.filter((b) => {
      const hasSameDepth = calculateDepth(b.path) === this.depth + 1;
      const hasSamePrefix = b.path.startsWith(this.path || "");

      return hasSameDepth && hasSamePrefix;
    });

    this.children = children.map((c) => new TreeNode(tree, this, c, branches));
  }

  traverseToPath(path: string | null): TreeNode | null {
    if (this.path === path) {
      return this;
    }

    for (const child of this.children) {
      const result = child.traverseToPath(path);

      if (result) {
        return result;
      }
    }

    return null;
  }

  getCategories(path: string | null): Category[] {
    if (this.isRoot) {
      return this.tree.branches.map((b) => {
        const seoTaxonomy = b.seoTaxonomies[b.seoTaxonomies.length - 1];
        return {
          href: b.path,
          slug: seoTaxonomy.slug,
          name: seoTaxonomy.name,
          isActive: false,
          isCurrent: false,
          count: b.totalRecipeCount,
          imageUrl: b.imageUrl,
        };
      });
    }

    const regex = new RegExp(`^${this.path}/[^/]+$`);

    return this.tree.branches
      .filter((b) => regex.test(b.path))
      .map((b) => {
        const isActive = path?.includes(b.path) ?? false;

        const href = isActive ? this.parent?.path ?? "" : b.path;

        const seoTaxonomy = b.seoTaxonomies[b.seoTaxonomies.length - 1];

        return {
          href,
          slug: seoTaxonomy.slug,
          name: seoTaxonomy.name,
          isActive,
          isCurrent: b.path === path,
          count: b.totalRecipeCount,
          imageUrl: b.imageUrl,
        };
      });
  }

  getAncestry(): Ancestor[] {
    if (!this.parent) {
      return [];
    }

    return this.parent.getAncestry().concat({
      slug: this.slug,
      path: this.path ?? "/",
      name: this.name,
      type: this.type,
    });
  }
}

export class Tree {
  public root: TreeNode;

  constructor(public branches: BranchLike[]) {
    this.root = new TreeNode(this, null, null, branches);
  }

  findNodeByPath(path: string | null): TreeNode | null {
    return this.root.traverseToPath(path);
  }

  getAllNodes(): FlatCategoryNode[] {
    const list: FlatCategoryNode[] = [];
    const collect = (node: TreeNode) => {
      if (!node.isRoot && node.path) {
        list.push({
          path: node.path,
          slug: node.slug,
          name: node.name,
          depth: node.depth,
          type: node.type,
          count: node.count,
        });
      }
      node.children.forEach(collect);
    };
    collect(this.root);
    return list;
  }
}
